import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './dashboard.css';
import {
  Box, Tabs, Tab, Select, MenuItem, FormControl, InputLabel,
  Tooltip,
  Alert
} from '@mui/material';
import Slider from 'react-slick';
import Header from '../../sharedcomponents/header/header';
import DeleteIcon from '@mui/icons-material/Delete';
import { Add } from '@mui/icons-material';
import Slide from '@mui/material/Slide';
import { getCurrentUserEmail } from '../../services/auth';
import { fetchApi } from '../../services/api';
import { setDivisionList } from '../../services/employeeStore';
import { onAccessLoaded } from '../../services/roleAccessService';
import DeleteConfirmation from '../../sharedcomponents/delete-confirmation/DeleteConfirmation';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, ChartTooltip, Legend);

const DEFAULT_MEMBER_ROLES = [
  { key: 'Responsible Person', value: 'Responsible Person' },
  { key: 'Originator', value: 'Originator' },
  { key: 'Coordinator', value: 'Coordinator' }
];

const Dashboard = ({
  tab1 = 'Closure Awaited',
  tab2 = 'Pending Form Completion',
  displayTab1 = true,
  displayTab2 = true,
  dashboardList = [],
  btnName = 'Check In',
  onDashboardClick,
  onViewAllClick,
  ngClassForStatus,
  toolTipForStatus,
  divisionList = [],
  memberRole = DEFAULT_MEMBER_ROLES,
  onDivisionChange,
  onRoleChange,
  draftedReports = []
}) => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState('Originator');
  const isSupplier = sessionStorage.getItem('user_type') === 'supplier'; // Supplier logic: check if current user is a Supplier
  const [selectedDivision, setSelectedDivision] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);
  const [fetchedDraftedReports, setFetchedDraftedReports] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, reportId: null });
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [tabType, setTabType] = useState('CLOSURE');
  const [currentBtnName, setCurrentBtnName] = useState('Edit Report');
  const [showTab1, setShowTab1] = useState(true);
  const [showTab2, setShowTab2] = useState(true);
  const [tab1Label, setTab1Label] = useState('Closure Awaited');
  const [tab2Label, setTab2Label] = useState('Pending Form Completion');
  const [fetchedDashboardList, setFetchedDashboardList] = useState([]);
  const [fetchedDivisionList, setFetchedDivisionList] = useState([]);
  const [roleList, setRoleList] = useState(memberRole);

  // Fetch roles from Role Master API
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetchApi('/api/RoleMaster/GetRoles', { method: 'GET' });
        let rolesData = [];
        if (Array.isArray(response)) rolesData = response;
        else if (response?.data) rolesData = response.data;
        else if (response?.result) rolesData = response.result;
        else if (response?.roles) rolesData = response.roles;

        const activeRoles = rolesData
          .filter(role => role.Status === 'Active')
          .map(role => ({ key: role.RoleDescription, value: role.RoleDescription }));

        if (activeRoles.length > 0) {
          setRoleList(activeRoles);
        }
      } catch (error) {
        console.error('Failed to fetch roles:', error);
      }
    };
    fetchRoles();
  }, []);

  // B2C users should always be set to Responsible Person role
  React.useEffect(() => {
    if (isSupplier) {
      setSelectedRole('Responsible Person');
      setTabType('PENDING');
      setShowTab1(true);
      setShowTab2(true);
      setCurrentBtnName('View More');
      setTab1Label('Pending Form Completion');
      setTab2Label('Awaiting Task Completion');
    }
  }, [isSupplier]);

  const fetchDraftedReports = async (role, division = '') => {
    try {
      // For B2C users selecting 'Responsible Person', use 'Responsible Person' to find D2-D7 assignments
      const apiRole = (isSupplier && role === 'Responsible Person') ? 'Responsible Person' : 
                     isSupplier ? 'Team Member' : role;
      
      const data = await fetchApi('/api/Dashboard/GetDraftedReports', {
        method: 'POST',
        body: JSON.stringify({
          EightDInfoId: 0,
          ReportNo: 'string',
          EmailId: getCurrentUserEmail(),
          MemberRole: apiRole,
          Division: division,
          pageSize: 0,
          pageNumber: 0
        })
      });
      const reports = data?.data || [];
      setFetchedDraftedReports(reports);
    } catch (error) {
    }
  };

  const fetchChartData = async (role, division = '') => {
    try {
      // For B2C users selecting 'Responsible Person', use 'Responsible Person' to find D2-D7 assignments
      const apiRole = (isSupplier && role === 'Responsible Person') ? 'Responsible Person' : 
                     isSupplier ? 'Team Member' : role;
      
      const data = await fetchApi('/api/Dashboard/GetChartData', {
        method: 'POST',
        body: JSON.stringify({
          EmailId: getCurrentUserEmail(),
          MemberRole: apiRole,
          Division: division
        })
      });
      const chartResult = data?.data || [];
      setChartData(chartResult);
    } catch (error) {
    }
  };

  const fetchDashboard = async (role, division = '', type = 'CLOSURE') => {
    try {
      // For B2C users selecting 'Responsible Person', use 'Responsible Person' to find D2-D7 assignments
      // For other roles or SSO users, use the selected role
      const apiRole = (isSupplier && role === 'Responsible Person') ? 'Responsible Person' : 
                     isSupplier ? 'Team Member' : role;
      
      const requestPayload = {
        EightDInfoId: 0,
        Division: division,
        ReportNo: 'string',
        ProblemStatement: 'string',
        PartDescription: 'string',
        D1Status: 'string',
        D2Status: 'string',
        D3Status: 'string',
        D4Status: 'string',
        D5Status: 'string',
        D6Status: 'string',
        D7Status: 'string',
        D8Status: 'string',
        EmailId: getCurrentUserEmail(),
        MemberRole: apiRole,
        Tracking: '',
        Type: type,
        pageSize: 0,
        pageNumber: 0
      };
      








      
      const data = await fetchApi('/api/Dashboard/GetDashboard', {
        method: 'POST',
        body: JSON.stringify(requestPayload)
      });
      





      if (type === 'AWAITING') {


      }

      
      const reports = data?.data || [];
      
      // Handle null data for AWAITING type
      if (type === 'AWAITING' && data?.data === null) {
        console.warn('AWAITING API returned null - possible backend issue');
        setFetchedDashboardList([]);
      } else {
        setFetchedDashboardList(reports);
      }
    } catch (error) {
      console.error('fetchDashboard error:', error);
    }
  };

  const fetchDivision = async (role, type = 'CLOSURE') => {
    try {
      // For B2C users selecting 'Responsible Person', use 'Responsible Person' to find D2-D7 assignments
      const apiRole = (isSupplier && role === 'Responsible Person') ? 'Responsible Person' : 
                     isSupplier ? 'Team Member' : role;
      
      const data = await fetchApi('/api/Dashboard/GetDivision', {
        method: 'POST',
        body: JSON.stringify({
          EightDInfoId: 0,
          Division: '',
          ReportNo: 'string',
          ProblemStatement: 'string',
          PartDescription: 'string',
          D1Status: 'string',
          D2Status: 'string',
          D3Status: 'string',
          D4Status: 'string',
          D5Status: 'string',
          D6Status: 'string',
          D7Status: 'string',
          D8Status: 'string',
          EmailId: getCurrentUserEmail(),
          MemberRole: apiRole,
          Tracking: '',
          Type: type,
          pageSize: 0,
          pageNumber: 0
        })
      });
      const divisions = data?.data || [];
      setFetchedDivisionList(divisions);
      setDivisionList(divisions);
    } catch (error) {
    }
  };

  useEffect(() => {
    // For B2C users, wait for the role to be set to 'Responsible Person'
    if (isSupplier && selectedRole !== 'Responsible Person') {
      return;
    }

    const loadData = () => {
      if (!getCurrentUserEmail()) return;
      fetchDraftedReports(selectedRole);
      fetchChartData(selectedRole);
      fetchDashboard(selectedRole, selectedDivision, tabType);
      fetchDivision(selectedRole, tabType);
    };

    // onAccessLoaded fires immediately if access already loaded, else waits for App.js to finish
    onAccessLoaded(loadData);
  }, [selectedRole, tabType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateNew = () => {
    navigate('/create-new');
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-GB');

  const handleRoleChange = (role) => {
    setSelectedTab(0);
    setSelectedRole(role);
    setSelectedDivision('');

    let newTabType;
    if (role === 'Responsible Person') {
      newTabType = 'PENDING';
      setTabType(newTabType);
      setShowTab1(true);
      setShowTab2(true);
      setCurrentBtnName('View More');
      setTab1Label('Pending Form Completion');
      setTab2Label('Awaiting Task Completion');
    } else {
      newTabType = 'CLOSURE';
      setTabType(newTabType);
      setShowTab1(true);
      setShowTab2(true);
      setCurrentBtnName('Edit Report');
      setTab1Label('Closure Awaited');
      setTab2Label('Pending Form Completion');
    }

    fetchDraftedReports(role, '');
    fetchChartData(role, '');
    fetchDashboard(role, '', newTabType);
    fetchDivision(role, newTabType);
    onRoleChange?.(role);
  };

  const handleDivisionChange = (division) => {
    setSelectedDivision(division);
    fetchDraftedReports(selectedRole, division);
    fetchChartData(selectedRole, division);
    fetchDashboard(selectedRole, division, tabType);
    onDivisionChange?.(division);
  };

  const handleTabChange = (e, newTab) => {
    setSelectedTab(newTab);

    let newTabType;
    if (selectedRole === 'Responsible Person') {
      newTabType = newTab === 0 ? 'PENDING' : 'AWAITING';
    } else if (newTab === 0 && showTab1) {
      newTabType = 'CLOSURE';
    } else {
      newTabType = 'PENDING';
    }

    // Debug logging for AWAITING issue







    setTabType(newTabType);
    fetchDashboard(selectedRole, selectedDivision, newTabType);
    fetchDivision(selectedRole, newTabType);
  };

  const getDoughnutData = () => {
    let closed = 0, pending = 0, reportClosure = 0;
    chartData.forEach(item => {
      if (item.Status === 'CLOSED') closed = item.StatusCount;
      if (item.Status === 'PENDINGFORMCOMPLETION') pending = item.StatusCount;
      if (item.Status === 'REPORTCLOSURE') reportClosure = item.StatusCount;
    });
    return {
      labels: ['Closed', 'Pending Form Completion', 'Report Closure'],
      datasets: [{
        data: [closed, pending, reportClosure],
        backgroundColor: ['#31d4ff', '#f8c239', '#ff4900'],
        borderColor: ['#31d4ff', '#f8c239', '#ff4900'],
        borderWidth: 0
      }]
    };
  };

  const doughnutOptions = {
    cutout: '88%',
    responsive: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        displayColors: false,
        callbacks: {
          label: (context) => context.parsed + '%'
        }
      }
    }
  };

  const getPendingPercent = () => {
    const item = chartData.find(d => d.Status === 'PENDINGFORMCOMPLETION');
    return item ? item.StatusCount : 0;
  };

  const sliderSettings = {
    slidesToShow: 4,
    slidesToScroll: 1,
    dots: true,
    infinite: false,
    variableWidth: false,
    arrows: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]
  };

  const getClassForStatus = (value, val) => {
    const v = (value || '').toUpperCase();
    if (v === 'COMPLETED') {
      return `${val} completed`;
    } else if (v) {
      return `${val} inprogress`;
    } else {
      return val;
    }
  };

  const getTooltipForStatus = (value) => {
    const v = (value || '').toUpperCase();
    if (v === 'COMPLETED') {
      return 'Completed';
    } else if (v === 'INPROCESS') {
      return 'Inprogress';
    } else if (v === 'PENDING' || v === 'PENDINGFORMCOMPLETION') {
      return 'Pending';
    } else {
      return '';
    }
  };

  const handleDashboardClick = async (dashboard) => {
    const eightDInfoId = dashboard.EightDInfoId || dashboard.eightDInfoId;
    try {
      const response = await fetchApi('/api/EightDHeader/Get8DHeader', {
        method: 'POST',
        body: JSON.stringify({ EightDInfoId: eightDInfoId })
      });

      const d1Response = await fetchApi('/api/D1/GetD1', {
        method: 'POST',
        body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) })
      });

      const roleOrder = { Originator: 0, Coordinator: 1 };
      const sortedD1Data = d1Response?.data ?
        [...d1Response.data].sort((a, b) => (roleOrder[a.MemberRole] ?? 2) - (roleOrder[b.MemberRole] ?? 2) || (a.D1Id || 0) - (b.D1Id || 0)) : [];

      navigate('/create-new', {
        state: {
          reportData: response?.data,
          d1Data: sortedD1Data,
          eightDInfoId: Number(eightDInfoId),
          currentUserEmail: getCurrentUserEmail(),
          mode: 'edit',
          isClosure: tabType === 'CLOSURE'
        }
      });
    } catch (error) {
      navigate('/create-new');
    }
  };

  const handleViewAllClick = () => {
    navigate('/8D-forms');
  };

  const renderReportCard = (dashboard) => {
    const reportNo = dashboard.ReportNo || dashboard.reportNo;
    const dateInitiated = dashboard.DateInitiated || dashboard.dateInitiated;
    const dateRevised = dashboard.DateRevised || dashboard.dateRevised;
    const problemStatement = dashboard.ProblemStatement || dashboard.problemStatement;
    const partDescription = dashboard.PartDescription || dashboard.partDescription;

    return (
      <Box key={reportNo} className="reportGrid">
        <div className="ReportDate">
          <div className="InitiatedDate">Date Initiated <span>{formatDate(dateInitiated)}</span></div>
          <div className="RevisedDate">Date Revised <span>{formatDate(dateRevised)}</span></div>
        </div>
        <div className="ReportId">
          <h2>Inspection Report No.:</h2>
          <p>{reportNo}</p>
        </div>
        <div className="problemStatement">
          <h2>Problem Statement:</h2>
          <p>{problemStatement}</p>
        </div>
        <div className="partDescriptions">
          <h2>Part Description:</h2>
          <p>{partDescription}</p>
        </div>
        <div className="status">
          <h2>Status:</h2>
          {['d1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8'].map((d) => {
            const status = dashboard[`${d}Status`] || dashboard[`${d.toUpperCase()}Status`];
            return (
              <Tooltip key={d} title={getTooltipForStatus(status)} placement="top">
                <div className={getClassForStatus(status, d)}>
                  <span></span>{d.toUpperCase()}
                </div>
              </Tooltip>
            );
          })}
        </div>
        <div className="checkInReport">
          <button type="button" onClick={() => handleDashboardClick(dashboard)}>{currentBtnName}</button>
        </div>
      </Box>
    );
  };

  return (
    <>
      <Header>
        <div className="contentSection">
          <form>
            <div className="mainContent">
              {/* Role Selector */}
              <div className="PageTitle">
                <span className="role-label" style={{ color: '#333333' }}>Select Role as</span>
                <div className="role-select-wrapper">
                  <FormControl variant="standard" fullWidth>
                    <Select
                      value={selectedRole}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      displayEmpty
                      className="role-select"
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            '& .MuiMenuItem-root:hover': {
                              backgroundColor: 'rgba(129, 0, 85, 0.1)',
                              color: '#810055'
                            }
                          }
                        }
                      }}
                    >
                      {roleList.map((item) => (
                        <MenuItem key={item.key} value={item.key}>
                          {item.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                {/* Supplier logic: hide Create New button for Supplier users */}
                {!isSupplier && (

                  <div className="addNew">
                    <button
                      type="button"
                      onClick={handleCreateNew}
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <Add className="material-icons">add</Add>
                    </button>
                  </div>
                )}
                {deleteSuccess && (
                  <Alert
                    onClose={() => setDeleteSuccess(false)}
                    severity="success"
                    variant="filled"
                    sx={{ ml: 2, maxWidth: 300 }}
                  >
                    Record deleted successfully
                  </Alert>
                )}
              </div>

              {/* Filters and Tabs */}
              <div className="pendingForms">
                <div className="dashboardsFilters" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {/* Tabs on the left */}
                  <div className="tabWrapper">
                    <div className="mat-tab-group">
                      <Tabs
                        value={selectedTab}
                        onChange={handleTabChange}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{
                          '& .MuiTab-root': {
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontFamily: 'GB-BodyCopy',
                            fontWeight: 600,
                            lineHeight: '20px',
                            color: '#999999 !important',
                            minHeight: '55px',
                            minWidth: '200px'
                          },
                          '& .Mui-selected': {
                            fontWeight: '900 !important',
                            color: '#333333 !important'
                          }
                        }}
                      >
                        {showTab1 && <Tab label={tab1Label} />}
                        {showTab2 && <Tab label={tab2Label} />}
                      </Tabs>
                    </div>
                  </div>

                  {/* Division dropdown on the right */}
                  <div className="divisionFilter">
                    <FormControl variant="standard" className="mat-form-field" fullWidth>
                      <Select
                        value={selectedDivision}
                        onChange={(e) => handleDivisionChange(e.target.value)}
                        displayEmpty
                        renderValue={(selected) =>
                          selected ? <span className="mat-option-text">{selected}</span> : <span className="mat-option-text">ALL</span>
                        }
                      >
                        <MenuItem value="">
                          <span className="mat-option-text">ALL</span>
                        </MenuItem>
                        {(fetchedDivisionList.length > 0 ? fetchedDivisionList : divisionList).map((d) => (
                          <MenuItem key={d.division || d.Division} value={d.division || d.Division}>
                            <span className="mat-option-text">{d.division || d.Division}</span>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="mat-tab-body-wrapper">
                  {(fetchedDashboardList.length > 0 || dashboardList.length > 0) ? (
                    <>
                      <Slider {...sliderSettings}>
                        {(fetchedDashboardList.length > 0 ? fetchedDashboardList : dashboardList).map((dashboard, index) => renderReportCard(dashboard))}
                      </Slider>
                      <button type="button" className="actionButton" onClick={handleViewAllClick}>View All</button>
                    </>
                  ) : (
                    <Slide direction="up" in={true} mountOnEnter unmountOnExit>
                      <div className="reportGrid noRecords"><p>No Record Found</p></div>
                    </Slide>
                  )}
                </div>
              </div>

              {/* Drafted Reports */}
              <section className="dashboardElements">
                {/* Left Chart */}
                <div className="leftchart">
                  <div className="dashboardOverview">
                    <div className="overviewTitle">
                      <h2 style={{ fontSize: '18px', fontWeight: 900, color: '#333333', fontFamily: 'GB-BodyCopy' }}>8D Reports Overview</h2>
                    </div>
                    <div className="clearfix" />
                    {chartData.length > 0 ? (
                      <div className="graph">
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                          <div style={{ flex: '0 0 50%', maxWidth: '50%', position: 'relative', textAlign: 'center' }}>
                            <div className="pieChart">
                              <Doughnut data={getDoughnutData()} options={doughnutOptions} height={160} width={160} />
                            </div>
                            <p className="pendingPercent">
                              Pending Form Completion <span>{getPendingPercent()}%</span>
                            </p>
                          </div>
                          <div className="graphInfo" style={{ flex: '0 0 50%', maxWidth: '50%' }}>
                            <ul>
                              <li className="pending">
                                <span></span>
                                Pending Form Completion <strong style={{ marginLeft: '8px', fontFamily: 'GB-BodyCopy' }}>{chartData.find(d => d.Status === 'PENDINGFORMCOMPLETION')?.StatusCount || 0}</strong>
                              </li>
                              <li className="reportClosue">
                                <span></span>
                                Report Closure <strong style={{ marginLeft: '8px', fontFamily: 'GB-BodyCopy' }}>{chartData.find(d => d.Status === 'REPORTCLOSURE')?.StatusCount || 0}</strong>
                              </li>
                              <li className="closed">
                                <span></span>
                                Closed <strong style={{ marginLeft: '8px', fontFamily: 'GB-BodyCopy' }}>{chartData.find(d => d.Status === 'CLOSED')?.StatusCount || 0}</strong>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Slide direction="up" in={true} mountOnEnter unmountOnExit>
                        <div className="reportGrid noRecords">
                          <p>No Record Found</p>
                        </div>
                      </Slide>
                    )}
                  </div>
                </div>

                {/* Right Table */}
                <div className="righttable">
                  <div className="drafted">
                    <h2 style={{ fontSize: '18px', fontWeight: 900, color: '#333333', fontFamily: 'GB-BodyCopy' }}>Drafted Reports</h2>
                    <div className="draftedTable">
                      {(fetchedDraftedReports.length > 0 || draftedReports.length > 0) ? (
                        <table style={{ borderbottom: '1px solid red', }}>
                          <thead>
                            <tr>
                              <th style={{ padding: '10px' }}>Inspection Report No.</th>
                              <th>Date Initiated</th>
                              <th>Date Revised</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(fetchedDraftedReports.length > 0 ? fetchedDraftedReports : draftedReports).map((report, index) => (
                              <tr key={report.ReportNo || report.reportNo || report.number || index}>
                                <td>{report.ReportNo || report.reportNo || report.number}</td>
                                <td>{new Date(report.DateInitiated).toLocaleDateString('en-GB')}</td>
                                <td>{new Date(report.DateRevised).toLocaleDateString('en-GB')}</td>
                                <td>
                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const eightDId = report.EightDInfoId || report.eightDInfoId;
                                      try {
                                        const response = await fetchApi('/api/EightDHeader/Get8DHeader', {
                                          method: 'POST',
                                          body: JSON.stringify({ EightDInfoId: eightDId })
                                        });

                                        // Fetch D1 data
                                        const d1Response = await fetchApi('/api/D1/GetD1', {
                                          method: 'POST',
                                          body: JSON.stringify({ EightDInfoId: Number(eightDId) })
                                        });

                                        const roleOrder = { Originator: 0, Coordinator: 1 };
                                        const sortedD1Data = d1Response?.data ?
                                          [...d1Response.data].sort((a, b) => (roleOrder[a.MemberRole] ?? 2) - (roleOrder[b.MemberRole] ?? 2) || (a.D1Id || 0) - (b.D1Id || 0)) : [];

                                        navigate('/create-new', {
                                          state: {
                                            reportData: response?.data,
                                            d1Data: sortedD1Data,
                                            eightDInfoId: Number(eightDId),
                                            currentUserEmail: getCurrentUserEmail(),
                                            mode: 'edit',
                                            isDrafted: true
                                          }
                                        });
                                      } catch (error) {
                                        navigate('/create-new');
                                      }
                                    }}
                                    style={{
                                      background: 'transparent',
                                      fontSize: '0.75rem',
                                      fontFamily: 'GB-Headline',
                                      lineHeight: '16px',
                                      color: '#810055',
                                      padding: '2px 10px',
                                      borderRadius: '30px',
                                      border: '1px solid #810055',
                                    }}
                                  >
                                    Click to Complete</button>
                                  <DeleteIcon
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDeleteDialog({ open: true, reportId: report.ReportNo || report.reportNo || report.number });
                                    }}
                                    style={{ cursor: 'pointer', marginLeft: '8px' }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p>No drafted reports available.</p>
                      )}
                    </div>
                    <div className="clearfix" />
                  </div>
                </div>
              </section>
            </div>
          </form>
        </div>
      </Header>
      <DeleteConfirmation
        open={deleteDialog.open}
        msg="Delete this record?"
        onClose={async (result) => {
          if (result === 'confirm') {
            try {
              await fetchApi('/api/Dashboard/DeleteEightDReport', {
                method: 'POST',
                body: JSON.stringify({ EightDInfoId: Number(deleteDialog.reportId) })
              });
              fetchDraftedReports(selectedRole, selectedDivision);
              setDeleteSuccess(true);
              setTimeout(() => setDeleteSuccess(false), 3000);
            } catch (error) {
            }
          }
          setDeleteDialog({ open: false, reportId: null });
        }}
      />
    </>
  );
};

export default Dashboard;
